/**
 * Payments Overview dashboard (DNKADM-0132) — the calm, informative landing tab at
 * `/settings/payments`. It composes the stored-state connections overview
 * (`GET /v2/admin/payments/connections/overview`, DNKAPI-0248) with the location
 * roster (`listLocations()`) per the handoff composition rule:
 *
 *  - Table 1 "Tenant default" (always): a master row for the tenant-default
 *    connection, and a detail row for every location that falls under it — a
 *    location with no own row, or an own row `servedBy:"tenant_default"` (or an
 *    unknown value), reads as a calm covered state; an own row `servedBy:"none"` is
 *    the ONE actionable "Needs reconnect" treatment.
 *  - Table 2 "Locations with their own connection" (only when a `servedBy:"self"`
 *    row exists): one row per individually-connected location.
 *
 * Driving principle: informative and unintimidating — a covered location is a
 * normal, served state, never an alarm; only `servedBy:"none"` warrants attention.
 * Every backend-sourced string (provider labels, connectedAccountName) is scrubbed
 * for the white-label brand at the render boundary.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  type ChipProps,
  Dialog,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import { listLocations, type V2Location } from '@/api/locations';
import {
  getPaymentsConnection,
  getPaymentsConnectionsOverview,
  getPaymentsRegistry,
  type PaymentsConnectionDescriptor,
  type PaymentsConnectionOverviewRow,
  type PaymentsConnectionsOverview,
  type PaymentsRegistry,
  type PaymentsStoredHealth,
} from '@/api/paymentsV2';
import { scrubBrand } from '@/utils/whitelabel';
import { useServiceLocation } from '@/location/useServiceLocation';
import {
  PaymentsChargeRulesDialog,
  PaymentsChargeRulesSummary,
  PaymentsConnectionEditor,
} from './PaymentsConnectionScreen';
import { ScreenError } from '@/components/ScreenError';

const DEFAULT_DRAWER_PARAM = 'configure';

type LoadState<T> =
  | { status: 'loading'; data: null; error: null }
  | { status: 'ready'; data: T; error: null }
  | { status: 'error'; data: null; error: unknown };

function loading<T>(): LoadState<T> {
  return { status: 'loading', data: null, error: null };
}

function ready<T>(data: T): LoadState<T> {
  return { status: 'ready', data, error: null };
}

function failed<T>(error: unknown): LoadState<T> {
  return { status: 'error', data: null, error };
}

interface OverviewData {
  overview: PaymentsConnectionsOverview;
  locations: V2Location[];
  /** Non-fatal: null when the registry read failed (labels fall back to raw codes). */
  registry: PaymentsRegistry | null;
}

interface ChargeRulesTarget {
  key: string;
  locationRef?: string;
}

interface ChargeRulesData {
  descriptor: PaymentsConnectionDescriptor;
  registry: PaymentsRegistry | null;
}

function loadOverviewData(): Promise<OverviewData> {
  return Promise.all([
    getPaymentsConnectionsOverview(),
    listLocations(),
    // Registry failure is non-fatal — labels fall back to title-cased raw codes.
    getPaymentsRegistry().catch((): PaymentsRegistry | null => null),
  ]).then(([overview, locations, registry]) => ({ overview, locations, registry }));
}

/** De-underscore/de-case a raw code into a readable label (mirrors the payments idiom). */
function humanizeCode(code: string): string {
  return code
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** A location's display name, falling back to its id when the name is empty. */
function locationName(loc: V2Location): string {
  return loc.name.length > 0 ? loc.name : loc.id;
}

interface HealthChipSpec {
  label: string;
  color: ChipProps['color'];
}

/** Stored-health → calm chip spec. Unknown values de-underscore verbatim, neutral. */
function healthChip(health: PaymentsStoredHealth): HealthChipSpec {
  switch (health) {
    case 'healthy_stored':
      return { label: 'Healthy', color: 'success' };
    case 'not_activated':
      return { label: 'Not activated', color: 'default' };
    case 'down':
      // Nuance on the connection itself is allowed; covered locations stay calm.
      return { label: 'Down', color: 'warning' };
    case 'unconfigured':
      return { label: 'Not configured', color: 'default' };
    default:
      return { label: health.replace(/[_-]+/g, ' ').trim(), color: 'default' };
  }
}

/** A calm stored-health Chip. */
function HealthChip({ health }: { health: PaymentsStoredHealth }) {
  const spec = healthChip(health);
  return <Chip size="small" label={spec.label} color={spec.color} variant="outlined" />;
}

function ConnectionCell({
  providerLabel,
  connectedAccountName,
}: {
  providerLabel: string | null;
  connectedAccountName: string | null;
}) {
  return (
    <Box>
      <Typography variant="body2" sx={providerLabel === null ? { fontWeight: 600 } : undefined}>
        {providerLabel ?? 'Not configured yet'}
      </Typography>
      {connectedAccountName !== null ? (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {scrubBrand(connectedAccountName)}
        </Typography>
      ) : null}
    </Box>
  );
}

function ConnectionEditorDialog({
  open,
  title,
  onClose,
  onConnectionChanged,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  onConnectionChanged: (locationRef: string | undefined) => void;
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="payments-connection-dialog-title"
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': 'payments-connection-dialog-title',
        },
      }}
    >
      <DialogTitle id="payments-connection-dialog-title" sx={{ pr: 10 }}>
        {title}
      </DialogTitle>
      <Button color="inherit" onClick={onClose} sx={{ position: 'absolute', top: 12, right: 16 }}>
        Close
      </Button>
      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        <PaymentsConnectionEditor onConnectionChanged={onConnectionChanged} />
      </DialogContent>
    </Dialog>
  );
}

function ChargeRulesAction({
  ariaLabel,
  target,
  summary,
  onOpen,
}: {
  ariaLabel: string;
  target: ChargeRulesTarget;
  summary: PaymentsConnectionDescriptor | undefined;
  onOpen: (target: ChargeRulesTarget, trigger: HTMLButtonElement) => void;
}) {
  return (
    <Box>
      <Button
        size="small"
        variant="outlined"
        aria-label={ariaLabel}
        onClick={(event) => onOpen(target, event.currentTarget)}
      >
        Charge rules
      </Button>
      {summary ? (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
          <PaymentsChargeRulesSummary descriptor={summary} />
        </Typography>
      ) : null}
    </Box>
  );
}

/** A covered detail row (falls under the tenant-default). */
interface CoveredRow {
  key: string;
  name: string;
  /** true = own row `servedBy:"none"` → the one actionable "Needs reconnect". */
  needsReconnect: boolean;
  /** The location id to deep-link the editor's Configure action to. */
  locationRef: string;
}

/** An individually-connected location row (`servedBy:"self"`). */
interface SelfRow {
  key: string;
  name: string;
  locationRef: string;
  row: PaymentsConnectionOverviewRow;
}

interface Composition {
  covered: CoveredRow[];
  self: SelfRow[];
}

/**
 * Group the roster + connection rows per the composition rule. A `self` own row →
 * Table 2; a `none` own row → a flagged Table 1 detail; everything else (no own row,
 * `tenant_default`, or unknown) → a calm Table 1 detail. Connection rows referencing
 * a location absent from the roster are shown by raw `locationRef` so a live
 * connection is never dropped from view.
 */
function compose(locations: V2Location[], connections: PaymentsConnectionOverviewRow[]): Composition {
  const ownByLocation = new Map<string, PaymentsConnectionOverviewRow>();
  for (const row of connections) {
    if (!row.isDefault && row.locationRef !== null) ownByLocation.set(row.locationRef, row);
  }

  const covered: CoveredRow[] = [];
  const self: SelfRow[] = [];
  const placed = new Set<string>();

  const place = (id: string, name: string, own: PaymentsConnectionOverviewRow | undefined) => {
    placed.add(id);
    if (own && own.servedBy === 'self') {
      self.push({ key: id, name, locationRef: id, row: own });
    } else if (own && own.servedBy === 'none') {
      covered.push({ key: id, name, needsReconnect: true, locationRef: id });
    } else {
      covered.push({ key: id, name, needsReconnect: false, locationRef: id });
    }
  };

  for (const loc of locations) {
    place(loc.id, locationName(loc), ownByLocation.get(loc.id));
  }
  // Orphan connection rows — a live connection whose location is not in the roster.
  for (const row of connections) {
    if (row.isDefault || row.locationRef === null || placed.has(row.locationRef)) continue;
    place(row.locationRef, row.locationRef, row);
  }

  return { covered, self };
}

export function PaymentsOverviewTab() {
  const { locations: contextLocations, status: locationStatus } = useServiceLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastConfigureTriggerRef = useRef<HTMLButtonElement | null>(null);
  const lastChargeRulesTriggerRef = useRef<HTMLButtonElement | null>(null);
  const chargeRulesRunRef = useRef(0);
  const overviewRunRef = useRef(0);
  const summaryRunRef = useRef<Record<string, number>>({});
  const [reloadKey, setReloadKey] = useState(0);
  const [state, setState] = useState<LoadState<OverviewData>>(loading());
  const [chargeRulesTarget, setChargeRulesTarget] = useState<ChargeRulesTarget | null>(null);
  const [chargeRulesState, setChargeRulesState] = useState<LoadState<ChargeRulesData>>(loading());
  const [chargeRulesSummaries, setChargeRulesSummaries] = useState<
    Record<string, PaymentsConnectionDescriptor>
  >({});

  const openLocationRef = searchParams.get('locationRef');
  const defaultDialogOpen = searchParams.get(DEFAULT_DRAWER_PARAM) === 'default';
  const dialogOpen = openLocationRef !== null || defaultDialogOpen || searchParams.has('connect');
  const anyDialogOpen = dialogOpen || chargeRulesTarget !== null;
  const pageRegistry = state.status === 'ready' ? state.data.registry : null;
  const contextLocation =
    openLocationRef === null ? null : contextLocations.find((location) => location.id === openLocationRef) ?? null;
  const baseDialogTitle =
    openLocationRef === null
      ? 'Company-wide default connection'
      : `Connection for ${contextLocation?.name || openLocationRef}`;

  const closeEditor = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('locationRef');
        next.delete(DEFAULT_DRAWER_PARAM);
        next.delete('connect');
        next.delete('reason');
        return next;
      },
      { replace: true },
    );
    window.requestAnimationFrame(() => lastConfigureTriggerRef.current?.focus());
  };

  const goToEditor = (locationRef: string | undefined, trigger: HTMLButtonElement) => {
    lastConfigureTriggerRef.current = trigger;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('connect');
      next.delete('reason');
      if (locationRef === undefined) {
        next.delete('locationRef');
        next.set(DEFAULT_DRAWER_PARAM, 'default');
      } else {
        next.set('locationRef', locationRef);
        next.delete(DEFAULT_DRAWER_PARAM);
      }
      return next;
    });
  };

  const loadChargeRules = async (target: ChargeRulesTarget, reloadProviderMetadata = false) => {
    const run = chargeRulesRunRef.current + 1;
    chargeRulesRunRef.current = run;
    // DNKADM-0437 V-21: this load is the newer truth for the row's cached summary; a slower
    // earlier summary read must not land over it.
    const summaryRun = (summaryRunRef.current[target.key] ?? 0) + 1;
    summaryRunRef.current[target.key] = summaryRun;
    setChargeRulesState(loading());
    try {
      const [descriptor, registry] = await Promise.all([
        getPaymentsConnection(target.locationRef),
        reloadProviderMetadata ? getPaymentsRegistry() : Promise.resolve(pageRegistry),
      ]);
      if (chargeRulesRunRef.current !== run) return;
      setChargeRulesState(ready({ descriptor, registry }));
      // Not if a save (or a newer load) has since taken the row's summary.
      if (summaryRunRef.current[target.key] === summaryRun) {
        setChargeRulesSummaries((current) => ({ ...current, [target.key]: descriptor }));
      }
    } catch (err) {
      if (chargeRulesRunRef.current === run) setChargeRulesState(failed(err));
    }
  };

  const openChargeRules = (target: ChargeRulesTarget, trigger: HTMLButtonElement) => {
    lastChargeRulesTriggerRef.current = trigger;
    setChargeRulesTarget(target);
    void loadChargeRules(target);
  };

  const closeChargeRules = () => {
    chargeRulesRunRef.current += 1;
    setChargeRulesTarget(null);
    window.requestAnimationFrame(() => lastChargeRulesTriggerRef.current?.focus());
  };

  useEffect(() => {
    const run = overviewRunRef.current + 1;
    overviewRunRef.current = run;
    setState(loading());
    loadOverviewData()
      .then((data) => {
        if (overviewRunRef.current === run) setState(ready(data));
      })
      .catch((err) => {
        if (overviewRunRef.current === run) setState(failed(err));
      });
    return () => {
      overviewRunRef.current += 1;
    };
  }, [reloadKey]);

  /**
   * DNKADM-0417 U3 — refetch in place after the connection changed (activate, deactivate, charge rules,
   * PIN, reconnect, disconnect) so the row Status, Health chip and the "Payments ready" banner update
   * with the dialog open or closed, without a page reload. Silent: the last good view stays up while it
   * loads, and a failed refetch keeps it rather than replacing the page with an error. It supersedes an
   * initial load still in flight, so when there is no good view yet a failure shows the retryable error
   * instead of leaving the skeleton up.
   */
  const refreshOverview = () => {
    const run = overviewRunRef.current + 1;
    overviewRunRef.current = run;
    loadOverviewData()
      .then((data) => {
        if (overviewRunRef.current === run) setState(ready(data));
      })
      .catch((err) => {
        if (overviewRunRef.current === run) {
          setState((current) => (current.status === 'ready' ? current : failed(err)));
        }
      });
  };

  /** Refetch one row's cached charge-rules summary; a slower earlier read never overwrites a newer one. */
  const refreshChargeRulesSummary = (target: ChargeRulesTarget) => {
    if (!(target.key in chargeRulesSummaries)) return;
    const run = (summaryRunRef.current[target.key] ?? 0) + 1;
    summaryRunRef.current[target.key] = run;
    getPaymentsConnection(target.locationRef)
      .then((descriptor) => {
        if (summaryRunRef.current[target.key] !== run) return;
        setChargeRulesSummaries((current) => ({ ...current, [target.key]: descriptor }));
      })
      .catch(() => {});
  };

  // The editor passes the location it changed, captured when the action ran — not whatever the URL says
  // by the time the re-read resolves.
  const handleConnectionChanged = (changedLocationRef: string | undefined) => {
    refreshOverview();
    refreshChargeRulesSummary(
      changedLocationRef === undefined
        ? { key: 'default' }
        : { key: `location:${changedLocationRef}`, locationRef: changedLocationRef },
    );
  };

  if (state.status === 'loading') {
    return (
      <>
        <Stack inert={dialogOpen ? true : undefined} sx={{ gap: 2, maxWidth: 900 }}>
          <Skeleton variant="rounded" height={56} role="status" aria-label="Loading payments overview" />
          <Skeleton variant="rounded" height={220} />
        </Stack>
        <ConnectionEditorDialog
          open={dialogOpen}
          title={baseDialogTitle}
          onClose={closeEditor}
          onConnectionChanged={handleConnectionChanged}
        />
      </>
    );
  }

  if (state.status === 'error') {
    return (
      <>
        <Box inert={dialogOpen ? true : undefined}>
          <ScreenError
            error={state.error}
            what="the payments overview"
            onRetry={() => setReloadKey((k) => k + 1)}
            preferServerMessage
            sx={{ maxWidth: 900, alignItems: 'center' }}
          />
        </Box>
        <ConnectionEditorDialog
          open={dialogOpen}
          title={baseDialogTitle}
          onClose={closeEditor}
          onConnectionChanged={handleConnectionChanged}
        />
      </>
    );
  }

  const { overview, locations, registry } = state.data;

  const providerLabelFor = (provider: string | null): string => {
    if (provider === null) return 'Provider';
    const fromRegistry = registry?.providers.find((p) => p.provider === provider)?.label ?? null;
    return scrubBrand(fromRegistry ?? humanizeCode(provider)) ?? humanizeCode(provider);
  };

  const defaultRow = overview.connections.find((row) => row.isDefault) ?? null;
  const { covered, self } = compose(locations, overview.connections);

  // A covered location reads as "Active …" only when a chargeable tenant-default
  // actually exists (a default row AND paymentsReady); otherwise the neutral
  // "Will use …" — never claiming served-ness the rollup denies.
  const coveredActive = defaultRow !== null && overview.paymentsReady;
  const coveredLabel = coveredActive
    ? 'Active (using tenant-default connection)'
    : 'Will use the tenant-default connection';

  const rollupText = overview.paymentsReady
    ? `Payments ready · ${overview.activeCount} active connection${overview.activeCount === 1 ? '' : 's'}`
    : 'Set up payments to start taking bookings';

  const simplifiedDefault = locationStatus === 'ready' && contextLocations.length === 1 && self.length === 0;
  const simplifiedLocation = simplifiedDefault ? contextLocations[0]! : null;
  const defaultRowOpen = (dialogOpen && openLocationRef === null) || chargeRulesTarget?.key === 'default';
  const loadedDialogLocation =
    openLocationRef === null ? null : locations.find((location) => location.id === openLocationRef) ?? null;
  const dialogTitle =
    simplifiedLocation !== null && openLocationRef === null
      ? `Company-wide default connection, currently serving ${simplifiedLocation.name || simplifiedLocation.id}`
      : openLocationRef === null
        ? baseDialogTitle
        : `Connection for ${loadedDialogLocation ? locationName(loadedDialogLocation) : contextLocation?.name || openLocationRef}`;
  const chargeRulesDescriptor = chargeRulesState.status === 'ready' ? chargeRulesState.data.descriptor : null;
  const chargeRulesRegistry = chargeRulesState.status === 'ready' ? chargeRulesState.data.registry : null;
  const chargeRulesProvider =
    chargeRulesDescriptor?.provider === null || chargeRulesDescriptor?.provider === undefined
      ? null
      : chargeRulesRegistry?.providers.find((provider) => provider.provider === chargeRulesDescriptor.provider) ?? null;
  const chargeRulesProviderUnavailable =
    chargeRulesDescriptor !== null && chargeRulesDescriptor.provider !== null && chargeRulesProvider === null;

  return (
    <>
      <Box inert={anyDialogOpen ? true : undefined}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" component="h1">
          Payments overview
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', maxWidth: 720 }}>
          Which connection serves which location — at a glance.
        </Typography>
      </Box>

      {/* Rollup — always calm/informational, never error or warning. */}
      <Alert severity="info" sx={{ mb: 2.5 }}>
        {rollupText}
      </Alert>

      {/* Table 1 — Tenant default (always rendered). */}
      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
        Tenant default
      </Typography>
      <TableContainer sx={{ mb: 4 }}>
        <Table aria-label="Tenant default" size="small">
          <TableHead>
            <TableRow>
              <TableCell>Location</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Connection</TableCell>
              <TableCell>Health</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Master row — the tenant-default connection (or the not-configured state). */}
            <TableRow selected={defaultRowOpen} aria-current={defaultRowOpen ? 'true' : undefined}>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {simplifiedLocation?.name || simplifiedLocation?.id || 'Company wide default'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{coveredActive ? 'Active (ready for payments)' : 'Not active'}</Typography>
              </TableCell>
              <TableCell>
                <ConnectionCell
                  providerLabel={defaultRow === null ? null : providerLabelFor(defaultRow.provider)}
                  connectedAccountName={defaultRow?.connectedAccountName ?? null}
                />
              </TableCell>
              <TableCell>{defaultRow !== null ? <HealthChip health={defaultRow.storedHealth} /> : null}</TableCell>
              <TableCell align="right">
                <Stack direction="row" sx={{ justifyContent: 'flex-end', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    aria-label={defaultRow !== null ? 'Configure tenant-default connection' : 'Set up payments'}
                    onClick={(event) => goToEditor(undefined, event.currentTarget)}
                  >
                    {defaultRow !== null ? 'Configure' : 'Set up payments'}
                  </Button>
                  <ChargeRulesAction
                    ariaLabel="Charge rules for tenant-default connection"
                    target={{ key: 'default' }}
                    summary={chargeRulesSummaries['default']}
                    onOpen={openChargeRules}
                  />
                </Stack>
              </TableCell>
            </TableRow>

            {/* Detail rows — every location under the tenant-default. */}
            {(simplifiedDefault ? [] : covered).map((row) => {
              const rowKey = `location:${row.locationRef}`;
              const selected = openLocationRef === row.locationRef || chargeRulesTarget?.key === rowKey;
              return (
              <TableRow key={row.key} selected={selected} aria-current={selected ? 'true' : undefined}>
                <TableCell sx={{ pl: 4 }}>{row.name}</TableCell>
                <TableCell>
                  {row.needsReconnect ? (
                    <Chip size="small" label="Needs reconnect" color="warning" variant="outlined" />
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {coveredLabel}
                    </Typography>
                  )}
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell align="right">
                  <Stack direction="row" sx={{ justifyContent: 'flex-end', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color={row.needsReconnect ? 'warning' : 'primary'}
                      aria-label={`Configure ${row.name}`}
                      onClick={(event) => goToEditor(row.locationRef, event.currentTarget)}
                    >
                      Configure
                    </Button>
                    <ChargeRulesAction
                      ariaLabel={`Charge rules for ${row.name}`}
                      target={{ key: rowKey, locationRef: row.locationRef }}
                      summary={chargeRulesSummaries[rowKey]}
                      onOpen={openChargeRules}
                    />
                  </Stack>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Table 2 — Locations with their own connection (only when any exist). */}
      {self.length > 0 ? (
        <>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
            Locations with their own connection
          </Typography>
          <TableContainer>
            <Table aria-label="Locations with their own connection" size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Location</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Connection</TableCell>
                  <TableCell>Health</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {self.map((entry) => {
                  const label = providerLabelFor(entry.row.provider);
                  const rowKey = `location:${entry.locationRef}`;
                  const selected = openLocationRef === entry.locationRef || chargeRulesTarget?.key === rowKey;
                  return (
                    <TableRow key={entry.key} selected={selected} aria-current={selected ? 'true' : undefined}>
                      <TableCell>{entry.name}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{`Active with ${label} (ready for payments)`}</Typography>
                      </TableCell>
                      <TableCell>
                        <ConnectionCell providerLabel={label} connectedAccountName={entry.row.connectedAccountName} />
                      </TableCell>
                      <TableCell>
                        <HealthChip health={entry.row.storedHealth} />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" sx={{ justifyContent: 'flex-end', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            aria-label={`Configure ${entry.name}`}
                            onClick={(event) => goToEditor(entry.locationRef, event.currentTarget)}
                          >
                            Configure
                          </Button>
                          <ChargeRulesAction
                            ariaLabel={`Charge rules for ${entry.name}`}
                            target={{ key: rowKey, locationRef: entry.locationRef }}
                            summary={chargeRulesSummaries[rowKey]}
                            onOpen={openChargeRules}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : null}
      </Box>
      <ConnectionEditorDialog
        open={dialogOpen}
        title={dialogTitle}
        onClose={closeEditor}
        onConnectionChanged={handleConnectionChanged}
      />
      <PaymentsChargeRulesDialog
        open={chargeRulesTarget !== null}
        onClose={closeChargeRules}
        locationRef={chargeRulesTarget?.locationRef}
        provider={chargeRulesProvider}
        descriptor={chargeRulesDescriptor}
        loading={chargeRulesState.status === 'loading'}
        loadError={chargeRulesState.status === 'error' ? chargeRulesState.error : null}
        providerUnavailable={chargeRulesProviderUnavailable}
        onRetry={() => {
          if (chargeRulesTarget !== null) void loadChargeRules(chargeRulesTarget, true);
        }}
        onSaved={(saved) => {
          if (chargeRulesTarget === null || chargeRulesState.status !== 'ready') return;
          setChargeRulesState(ready({ descriptor: saved, registry: chargeRulesState.data.registry }));
          setChargeRulesSummaries((current) => ({ ...current, [chargeRulesTarget.key]: saved }));
          // DNKADM-0437 V-21: drop any summary read still in flight; it predates this save.
          summaryRunRef.current[chargeRulesTarget.key] =
            (summaryRunRef.current[chargeRulesTarget.key] ?? 0) + 1;
          // Saving charge rules can change what the row, Health chip and banner report (DNKADM-0417 U3).
          refreshOverview();
        }}
      />
    </>
  );
}
